// src/products/equityManager/pages/WhatIfScenarioPage.js
import React, { useState, useMemo } from 'react';
import { Calculator, TrendingDown, AlertCircle, DollarSign } from 'lucide-react';
import MagnitudeSelector, { useMagnitude } from '../../../components/ui/MagnitudeSelector';
import CurrencyService from '../../../services/currencyService';

const WhatIfScenarioPage = ({ 
    selectedCompany, 
    shareholders, 
    shareClasses, 
    issuances,
    addMessage,
    addSuccess,
    addError 
}) => {
    // Input state
    const [scenarioName, setScenarioName] = useState('New Investment Round');
    const [targetValuation, setTargetValuation] = useState('');
    const [capitalRaise, setCapitalRaise] = useState('');
    const [instrumentType, setInstrumentType] = useState('common');
    const [magnitude, setMagnitude] = useState('millions'); // 'units', 'thousands', 'millions'
    const [liquidationPreference, setLiquidationPreference] = useState('1'); // 1x, 2x, 3x
    const [selectedExitScenario, setSelectedExitScenario] = useState('2'); // Index for shareholder breakdown (default: Success 2x)

    // Use magnitude utilities hook
    const {
        multiplier: magnitudeMultiplier,
        label: magnitudeLabel,
        getPlaceholder,
        getStepValue,
        formatCurrency
    } = useMagnitude(magnitude);

    // Legacy helper for backward compatibility
    const getMagnitudeMultiplier = () => magnitudeMultiplier;
    const getMagnitudeLabel = () => magnitudeLabel;

    // Get company currency info
    const companyCurrency = selectedCompany?.currency || 'USD';
    const currencyInfo = CurrencyService.getCurrencyByCode(companyCurrency);
    
    // Custom currency formatting functions that use company currency
    const formatCurrencyWithCompanyCurrency = (amount) => {
        return CurrencyService.formatAmount(amount, companyCurrency, 'en-US');
    };
    
    const formatCurrencyWithCompanyCurrencyAndMagnitude = (amount) => {
        // Convert the amount back to the input scale for proper formatting
        const scaledAmount = amount / magnitudeMultiplier;
        const currencySymbol = CurrencyService.getCurrencySymbol(companyCurrency);
        // Use 1 decimal place for thousands and millions, 0 for units
        const decimals = (magnitude === 'thousands' || magnitude === 'millions') ? 1 : 0;
        return `${currencySymbol}${formatNumber(scaledAmount, decimals)}${magnitudeLabel}`;
    };

    // Calculate current shareholding structure
    const currentHoldings = useMemo(() => {
        if (!issuances || !shareholders || issuances.length === 0) {
            return { totalShares: 0, holders: [], totalValue: 0 };
        }

        // Group shares by shareholder
        const holdingsByHolder = {};
        
        issuances.forEach(issuance => {
            const shareholder = shareholders.find(sh => sh.id === issuance.shareholder_id);
            const shareholderName = shareholder?.name || 'Unknown';
            const shareholderId = shareholder?.id || 'unknown';

            if (!holdingsByHolder[shareholderId]) {
                holdingsByHolder[shareholderId] = {
                    id: shareholderId,
                    name: shareholderName,
                    shares: 0,
                    value: 0
                };
            }

            holdingsByHolder[shareholderId].shares += issuance.shares || 0;
            holdingsByHolder[shareholderId].value += issuance.total_value || 0;
        });

        const holders = Object.values(holdingsByHolder);
        const totalShares = holders.reduce((sum, h) => sum + h.shares, 0);
        const totalValue = holders.reduce((sum, h) => sum + h.value, 0);

        // Calculate percentages
        holders.forEach(holder => {
            holder.percentage = totalShares > 0 ? (holder.shares / totalShares) * 100 : 0;
        });

        // Sort by percentage descending
        holders.sort((a, b) => b.percentage - a.percentage);

        return { totalShares, holders, totalValue };
    }, [issuances, shareholders]);

    // Calculate new scenario
    const newScenario = useMemo(() => {
        const valuation = (parseFloat(targetValuation) || 0) * magnitudeMultiplier;
        const raise = (parseFloat(capitalRaise) || 0) * magnitudeMultiplier;

        if (valuation <= 0 || raise <= 0 || currentHoldings.totalShares === 0) {
            return null;
        }

        // Calculate new shares to be issued
        const pricePerShare = valuation / currentHoldings.totalShares;
        const newShares = raise / pricePerShare;
        const newTotalShares = currentHoldings.totalShares + newShares;

        // Calculate new holdings
        const newHolders = currentHoldings.holders.map(holder => ({
            ...holder,
            newShares: holder.shares, // They keep the same number of shares
            newPercentage: (holder.shares / newTotalShares) * 100,
            dilution: holder.percentage - (holder.shares / newTotalShares) * 100
        }));

        // Add the new investor
        newHolders.push({
            id: 'new-investor',
            name: 'New Investor',
            shares: 0,
            percentage: 0,
            newShares: newShares,
            newPercentage: (newShares / newTotalShares) * 100,
            dilution: 0,
            isNew: true
        });

        // Sort by new percentage
        newHolders.sort((a, b) => b.newPercentage - a.newPercentage);

        return {
            pricePerShare,
            newShares,
            newTotalShares,
            postMoneyValuation: valuation + raise,
            holders: newHolders
        };
    }, [targetValuation, capitalRaise, currentHoldings, magnitudeMultiplier]);

    // Calculate sensitivity analysis
    const sensitivityAnalysis = useMemo(() => {
        if (!newScenario) return null;

        const baseValuation = (parseFloat(targetValuation) || 0) * magnitudeMultiplier;
        const raise = (parseFloat(capitalRaise) || 0) * magnitudeMultiplier;
        const variations = [
            { label: '-20%', multiplier: 0.8 },
            { label: '-10%', multiplier: 0.9 },
            { label: '-5%', multiplier: 0.95 },
            { label: 'Base', multiplier: 1.0 },
            { label: '+5%', multiplier: 1.05 },
            { label: '+10%', multiplier: 1.1 },
            { label: '+20%', multiplier: 1.2 }
        ];

        return variations.map(v => {
            const adjustedValuation = baseValuation * v.multiplier;
            const pricePerShare = adjustedValuation / currentHoldings.totalShares;
            const newShares = raise / pricePerShare;
            const newTotalShares = currentHoldings.totalShares + newShares;
            const dilution = (newShares / newTotalShares) * 100;
            const investorOwnership = (newShares / newTotalShares) * 100;

            return {
                label: v.label,
                valuation: adjustedValuation,
                pricePerShare,
                newShares,
                dilution,
                investorOwnership,
                isBase: v.multiplier === 1.0
            };
        });
    }, [targetValuation, capitalRaise, currentHoldings, newScenario, magnitudeMultiplier]);

    // Calculate liquidation/exit scenarios
    const liquidationScenarios = useMemo(() => {
        if (!newScenario) return null;

        const raise = (parseFloat(capitalRaise) || 0) * magnitudeMultiplier;
        const preferenceMultiplier = parseFloat(liquidationPreference) || 1;
        const investorPreference = raise * preferenceMultiplier;
        
        const isPreferred = instrumentType === 'preferred';

        // Calculate three exit scenarios: Low, Mid, High
        const postMoney = newScenario.postMoneyValuation;
        const scenarios = [
            { label: 'Down Round', multiplier: 0.5, exitValue: postMoney * 0.5 },
            { label: 'Break Even', multiplier: 1.0, exitValue: postMoney },
            { label: 'Success (2x)', multiplier: 2.0, exitValue: postMoney * 2 },
            { label: 'Big Exit (5x)', multiplier: 5.0, exitValue: postMoney * 5 }
        ];

        const results = scenarios.map(scenario => {
            const exitValue = scenario.exitValue;
            let investorReturn, existingReturn;

            if (isPreferred) {
                // Preferred: Get liquidation preference first, then participate in remainder
                if (exitValue <= investorPreference) {
                    // Exit is less than preference - investor gets everything (up to exit value)
                    investorReturn = Math.min(exitValue, investorPreference);
                    existingReturn = exitValue - investorReturn;
                } else {
                    // Exit exceeds preference
                    // Option 1: Take preference
                    const takePreference = investorPreference;
                    const remainingAfterPref = exitValue - investorPreference;
                    const participateInRemaining = remainingAfterPref * (newScenario.newShares / newScenario.newTotalShares);
                    
                    // Investor takes participating preferred (preference + pro-rata share of remainder)
                    investorReturn = takePreference + participateInRemaining;
                    existingReturn = exitValue - investorReturn;
                }
            } else {
                // Common: Simple pro-rata split
                investorReturn = exitValue * (newScenario.newShares / newScenario.newTotalShares);
                existingReturn = exitValue - investorReturn;
            }

            const investorROI = raise > 0 ? ((investorReturn - raise) / raise) * 100 : 0;
            const investorMultiple = raise > 0 ? investorReturn / raise : 0;

            return {
                ...scenario,
                investorReturn,
                existingReturn,
                investorROI,
                investorMultiple,
                investorPercent: (investorReturn / exitValue) * 100,
                existingPercent: (existingReturn / exitValue) * 100
            };
        });

        return {
            investorPreference,
            isPreferred,
            scenarios: results
        };
    }, [newScenario, capitalRaise, instrumentType, liquidationPreference, magnitudeMultiplier]);

    // Calculate shareholder-by-shareholder breakdown for selected exit scenario
    const shareholderExitBreakdown = useMemo(() => {
        if (!liquidationScenarios || !newScenario || !currentHoldings.holders) return null;

        const scenarioIndex = parseInt(selectedExitScenario);
        const selectedScenario = liquidationScenarios.scenarios[scenarioIndex];
        
        if (!selectedScenario) return null;

        const exitValue = selectedScenario.exitValue;
        const raise = (parseFloat(capitalRaise) || 0) * magnitudeMultiplier;
        const preferenceMultiplier = parseFloat(liquidationPreference) || 1;
        const investorPreference = raise * preferenceMultiplier;
        const isPreferred = instrumentType === 'preferred';

        let remainingValue = exitValue;
        const breakdown = [];

        // Step 1: Handle liquidation preference for preferred stock
        let preferenceUsed = 0;
        if (isPreferred && remainingValue > 0) {
            preferenceUsed = Math.min(investorPreference, remainingValue);
            remainingValue -= preferenceUsed;
        }

        // Step 2: Distribute remaining value pro-rata
        const totalShares = newScenario.newTotalShares;

        // Add existing shareholders
        currentHoldings.holders.forEach(holder => {
            const shares = holder.shares;
            const ownershipPercent = (shares / totalShares) * 100;
            const proRataShare = (remainingValue * shares) / totalShares;
            
            breakdown.push({
                id: holder.id,
                name: holder.name,
                type: 'Existing',
                shares: shares,
                newShares: shares, // They keep same shares
                ownershipPercent: ownershipPercent,
                preferenceAmount: 0,
                proRataAmount: proRataShare,
                totalReturn: proRataShare,
                percentOfExit: (proRataShare / exitValue) * 100
            });
        });

        // Add new investor
        const investorShares = newScenario.newShares;
        const investorOwnership = (investorShares / totalShares) * 100;
        const investorProRata = (remainingValue * investorShares) / totalShares;
        const investorTotal = preferenceUsed + investorProRata;

        breakdown.push({
            id: 'new-investor',
            name: 'New Investor',
            type: 'New',
            shares: 0,
            newShares: investorShares,
            ownershipPercent: investorOwnership,
            preferenceAmount: preferenceUsed,
            proRataAmount: investorProRata,
            totalReturn: investorTotal,
            percentOfExit: (investorTotal / exitValue) * 100,
            isNew: true,
            invested: raise,
            roi: raise > 0 ? ((investorTotal - raise) / raise) * 100 : 0,
            multiple: raise > 0 ? investorTotal / raise : 0
        });

        // Sort by total return descending
        breakdown.sort((a, b) => b.totalReturn - a.totalReturn);

        return {
            exitValue,
            scenarioLabel: selectedScenario.label,
            breakdown,
            isPreferred
        };
    }, [liquidationScenarios, newScenario, currentHoldings, selectedExitScenario, capitalRaise, instrumentType, liquidationPreference, magnitudeMultiplier]);

    // Format number with commas
    const formatNumber = (value, decimals = 0) => {
        if (!value && value !== 0) return '0';
        return parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    if (!selectedCompany) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <Calculator className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
                    <p className="text-gray-600">Please select a company to run what-if scenarios</p>
                </div>
            </div>
        );
    }

    if (currentHoldings.totalShares === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Share Data Available</h3>
                    <p className="text-gray-600">Please add share issuances before running scenarios</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Calculator className="w-8 h-8 mr-3 text-blue-600" />
                    What-If Scenario Tool
                </h1>
                <p className="text-gray-600 mt-1">
                    Model capital raising scenarios and see the impact on shareholdings
                </p>
            </div>

            {/* Two Column Layout - 1/3 inputs, 2/3 outputs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT SIDE - Inputs */}
                <div className="space-y-6">
                    {/* Input Form Card */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Scenario Inputs</h2>
                        
                        <div className="space-y-4">
                            {/* Scenario Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Scenario Name
                                </label>
                                <input
                                    type="text"
                                    value={scenarioName}
                                    onChange={(e) => setScenarioName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., Series A Round"
                                />
                            </div>

                            {/* Magnitude Selector */}
                            <MagnitudeSelector 
                                value={magnitude}
                                onChange={setMagnitude}
                            />

                            {/* Target Valuation */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Pre-Money Valuation ({getMagnitudeLabel() ? `${currencyInfo?.symbol || '$'}${getMagnitudeLabel()}` : currencyInfo?.symbol || '$'})
                                </label>
                                <input
                                    type="number"
                                    value={targetValuation}
                                    onChange={(e) => setTargetValuation(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={getPlaceholder(5000000)}
                                    min="0"
                                    step={getStepValue()}
                                />
                                {targetValuation && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formatCurrencyWithCompanyCurrency(parseFloat(targetValuation) * getMagnitudeMultiplier())}
                                    </p>
                                )}
                            </div>

                            {/* Capital Raise */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Target Capital Raise ({getMagnitudeLabel() ? `${currencyInfo?.symbol || '$'}${getMagnitudeLabel()}` : currencyInfo?.symbol || '$'})
                                </label>
                                <input
                                    type="number"
                                    value={capitalRaise}
                                    onChange={(e) => setCapitalRaise(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={getPlaceholder(1000000)}
                                    min="0"
                                    step={getStepValue()}
                                />
                                {capitalRaise && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formatCurrencyWithCompanyCurrency(parseFloat(capitalRaise) * getMagnitudeMultiplier())}
                                    </p>
                                )}
                            </div>

                            {/* Instrument Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Instrument Type
                                </label>
                                <select
                                    value={instrumentType}
                                    onChange={(e) => setInstrumentType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="common">Common Stock</option>
                                    <option value="preferred">Preferred Stock</option>
                                    <option value="convertible">Convertible Note</option>
                                    <option value="safe">SAFE</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {instrumentType === 'preferred' 
                                        ? 'Preferred stock includes liquidation preference in exit scenarios' 
                                        : 'Affects exit/liquidation modeling shown below'}
                                </p>
                            </div>

                            {/* Liquidation Preference - Only show for Preferred */}
                            {instrumentType === 'preferred' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Liquidation Preference
                                    </label>
                                    <select
                                        value={liquidationPreference}
                                        onChange={(e) => setLiquidationPreference(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="1">1x (Standard)</option>
                                        <option value="1.5">1.5x</option>
                                        <option value="2">2x</option>
                                        <option value="3">3x</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Investor gets {liquidationPreference}x their investment back before others in an exit
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Quick Calculation Display */}
                        {newScenario && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Summary</h3>
                                
                                {/* New Investor Equity - Highlighted */}
                                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-xs text-green-700 font-medium mb-1">New Investor Ownership</p>
                                    <p className="text-2xl font-bold text-green-700">
                                        {((newScenario.newShares / newScenario.newTotalShares) * 100).toFixed(2)}%
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        {formatNumber(newScenario.newShares, 0)} of {formatNumber(newScenario.newTotalShares, 0)} shares
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-gray-600">Price per Share</p>
                                        <p className="font-semibold text-gray-900">
                                            {formatCurrency(newScenario.pricePerShare)} {companyCurrency}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">New Shares</p>
                                        <p className="font-semibold text-gray-900">
                                            {formatNumber(newScenario.newShares, 0)} shares
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Post-Money Valuation</p>
                                        <p className="font-semibold text-blue-600">
                                            {formatCurrencyWithCompanyCurrencyAndMagnitude(newScenario.postMoneyValuation)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600">Total Shares</p>
                                        <p className="font-semibold text-gray-900">
                                            {formatNumber(newScenario.newTotalShares, 0)} shares
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Current Cap Table */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Cap Table</h2>
                        <div className="space-y-2">
                            {currentHoldings.holders.slice(0, 5).map(holder => (
                                <div key={holder.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{holder.name}</p>
                                        <p className="text-xs text-gray-500">
                                            {formatNumber(holder.shares, 0)} shares
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-semibold text-gray-900">
                                            {holder.percentage.toFixed(2)}%
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {currentHoldings.holders.length > 5 && (
                                <p className="text-xs text-gray-500 text-center pt-2">
                                    + {currentHoldings.holders.length - 5} more shareholders
                                </p>
                            )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between text-sm font-semibold">
                                <span>Total</span>
                                <span>{formatNumber(currentHoldings.totalShares, 0)} shares</span>
                            </div>
                        </div>
                    </div>

                    {/* Scenario Cap Table */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Scenario Cap Table</h2>
                        {newScenario ? (
                            <div className="space-y-2">
                                {newScenario.holders.map(holder => (
                                    <div key={holder.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                                        <div>
                                            <p className={`text-sm font-medium ${holder.isNew ? 'text-green-700' : 'text-gray-900'}`}>
                                                {holder.name}
                                                {holder.isNew && (
                                                    <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                                        New
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatNumber(holder.isNew ? holder.newShares : holder.shares, 0)} shares
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-sm font-semibold ${holder.isNew ? 'text-green-700' : 'text-gray-900'}`}>
                                                {holder.isNew ? holder.newPercentage.toFixed(2) : holder.percentage.toFixed(2)}%
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">No scenario model added yet</p>
                                <p className="text-xs mt-1">Enter valuation and capital raise to see scenario cap table</p>
                            </div>
                        )}
                        {newScenario && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex justify-between text-sm font-semibold">
                                    <span>Total</span>
                                    <span>{formatNumber(newScenario.newTotalShares, 0)} shares</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT SIDE - Visual Output (spans 2 columns) */}
                <div className="lg:col-span-2 space-y-6">
                    {newScenario ? (
                        <>
                            {/* Shareholding Comparison */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    Shareholding Impact
                                </h2>
                                
                                <div className="space-y-3">
                                    {newScenario.holders.map(holder => (
                                        <div key={holder.id} className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <p className={`text-sm font-medium ${holder.isNew ? 'text-green-700' : 'text-gray-900'}`}>
                                                        {holder.name}
                                                        {holder.isNew && (
                                                            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                                                New
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {holder.percentage.toFixed(2)}% → {holder.newPercentage.toFixed(2)}%
                                                    </p>
                                                    {!holder.isNew && (
                                                        <p className={`text-xs flex items-center justify-end ${holder.dilution > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                                            {holder.dilution > 0 && <TrendingDown className="w-3 h-3 mr-1" />}
                                                            {holder.dilution > 0 ? `-${holder.dilution.toFixed(2)}%` : 'No change'}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Visual bar */}
                                            <div className="h-6 bg-gray-100 rounded-md overflow-hidden relative">
                                                {/* Old percentage (gray) */}
                                                {!holder.isNew && (
                                                    <div 
                                                        className="h-full bg-blue-200 absolute top-0 left-0 transition-all"
                                                        style={{ width: `${holder.percentage}%` }}
                                                    />
                                                )}
                                                {/* New percentage (colored) */}
                                                <div 
                                                    className={`h-full absolute top-0 left-0 transition-all ${
                                                        holder.isNew ? 'bg-green-500' : 'bg-blue-500'
                                                    }`}
                                                    style={{ width: `${holder.newPercentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Legend:</span>
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-blue-200 rounded"></div>
                                                <span className="text-xs text-gray-600">Before</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                                                <span className="text-xs text-gray-600">After</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-green-500 rounded"></div>
                                                <span className="text-xs text-gray-600">New Investor</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sensitivity Analysis */}
                            {sensitivityAnalysis && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                        Valuation Sensitivity Analysis
                                    </h2>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Impact of valuation changes on dilution and ownership
                                    </p>
                                    
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-200">
                                                    <th className="text-left py-2 pr-4 text-gray-700 font-semibold">Scenario</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">Valuation</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">Price/Share</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">Dilution</th>
                                                    <th className="text-right py-2 pl-2 text-gray-700 font-semibold">Investor %</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sensitivityAnalysis.map((row, idx) => (
                                                    <tr 
                                                        key={idx} 
                                                        className={`border-b border-gray-100 ${row.isBase ? 'bg-blue-50 font-semibold' : ''}`}
                                                    >
                                                        <td className="py-2 pr-4">
                                                            {row.label}
                                                            {row.isBase && (
                                                                <span className="ml-2 text-xs text-blue-600">(Current)</span>
                                                            )}
                                                        </td>
                                                        <td className="text-right py-2 px-2">
                                                            {formatCurrencyWithCompanyCurrencyAndMagnitude(row.valuation)}
                                                        </td>
                                                        <td className="text-right py-2 px-2 font-mono text-xs">
                                                            {row.pricePerShare >= 1 ? `${formatCurrency(row.pricePerShare)} ${companyCurrency}` : `$${row.pricePerShare.toFixed(4)} ${companyCurrency}`}
                                                        </td>
                                                        <td className="text-right py-2 px-2">
                                                            <span className={row.dilution > 25 ? 'text-red-600' : row.dilution > 15 ? 'text-yellow-600' : 'text-green-600'}>
                                                                {row.dilution.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                        <td className="text-right py-2 pl-2">
                                                            {row.investorOwnership.toFixed(2)}%
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <div className="flex items-start space-x-2">
                                            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-gray-600">
                                                <strong>Note:</strong> Lower valuations result in higher dilution as more shares 
                                                must be issued to raise the same amount of capital.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Exit/Liquidation Scenarios */}
                            {liquidationScenarios && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">
                                                Exit Value Scenarios
                                            </h2>
                                            <p className="text-xs text-gray-600 mt-1">
                                                {liquidationScenarios.isPreferred 
                                                    ? `Preferred stock with ${liquidationPreference}x liquidation preference (${formatCurrencyWithCompanyCurrencyAndMagnitude(liquidationScenarios.investorPreference)})`
                                                    : 'Common stock - pro-rata split based on ownership'}
                                            </p>
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                            liquidationScenarios.isPreferred 
                                                ? 'bg-purple-100 text-purple-700' 
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {liquidationScenarios.isPreferred ? 'Preferred' : 'Common'}
                                        </div>
                                    </div>
                                    
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-200">
                                                    <th className="text-left py-2 pr-4 text-gray-700 font-semibold">Exit Scenario</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">Exit Value</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">New Investor</th>
                                                    <th className="text-right py-2 px-2 text-gray-700 font-semibold">ROI</th>
                                                    <th className="text-right py-2 pl-2 text-gray-700 font-semibold">Existing</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {liquidationScenarios.scenarios.map((scenario, idx) => (
                                                    <tr 
                                                        key={idx} 
                                                        className={`border-b border-gray-100 hover:bg-gray-50 ${scenario.isCustom ? 'bg-blue-50' : ''}`}
                                                    >
                                                        <td className="py-3 pr-4 font-medium">
                                                            {scenario.label}
                                                            {scenario.isCustom && (
                                                                <span className="ml-2 text-xs text-blue-600">(Custom)</span>
                                                            )}
                                                        </td>
                                                        <td className="text-right py-3 px-2 font-semibold">
                                                            {formatCurrencyWithCompanyCurrencyAndMagnitude(scenario.exitValue)}
                                                        </td>
                                                        <td className="text-right py-3 px-2">
                                                            <div>
                                                                <p className="font-semibold text-green-700">
                                                                    {formatCurrencyWithCompanyCurrencyAndMagnitude(scenario.investorReturn)}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    ({scenario.investorPercent.toFixed(1)}%)
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="text-right py-3 px-2">
                                                            <div>
                                                                <p className={`font-semibold ${
                                                                    scenario.investorROI >= 100 ? 'text-green-600' : 
                                                                    scenario.investorROI >= 0 ? 'text-gray-700' : 
                                                                    'text-red-600'
                                                                }`}>
                                                                    {scenario.investorROI >= 0 ? '+' : ''}{scenario.investorROI.toFixed(0)}%
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {scenario.investorMultiple.toFixed(2)}x
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="text-right py-3 pl-2">
                                                            <div>
                                                                <p className="font-semibold text-blue-700">
                                                                    {formatCurrencyWithCompanyCurrencyAndMagnitude(scenario.existingReturn)}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    ({scenario.existingPercent.toFixed(1)}%)
                                                                </p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {liquidationScenarios.isPreferred && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="flex items-start space-x-2">
                                                <AlertCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-gray-600">
                                                    <strong>Preferred Stock Impact:</strong> The new investor receives their liquidation 
                                                    preference ({liquidationPreference}x = {formatCurrencyWithCompanyCurrencyAndMagnitude(liquidationScenarios.investorPreference)}) 
                                                    first, then participates pro-rata in remaining proceeds. This protects downside risk but 
                                                    may reduce returns in smaller exits for existing shareholders.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {!liquidationScenarios.isPreferred && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="flex items-start space-x-2">
                                                <AlertCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-gray-600">
                                                    <strong>Common Stock:</strong> Exit proceeds are split pro-rata based on ownership 
                                                    percentage. All shareholders are treated equally with no preference.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Shareholder-by-Shareholder Breakdown */}
                            {shareholderExitBreakdown && liquidationScenarios && (
                                <div className="bg-white rounded-lg shadow-md p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Shareholder Exit Distribution
                                        </h2>
                                        
                                        {/* Exit Scenario Selector */}
                                        <div className="flex items-center space-x-3">
                                            <label className="text-sm text-gray-600">Exit Scenario:</label>
                                            <select
                                                value={selectedExitScenario}
                                                onChange={(e) => setSelectedExitScenario(e.target.value)}
                                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                {liquidationScenarios.scenarios.map((scenario, idx) => (
                                                    <option key={idx} value={idx}>
                                                        {scenario.label} ({formatCurrencyWithCompanyCurrencyAndMagnitude(scenario.exitValue)})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-600 mb-4">
                                        Detailed breakdown showing how {formatCurrencyWithCompanyCurrencyAndMagnitude(shareholderExitBreakdown.exitValue)} exit 
                                        value is distributed among all shareholders
                                    </p>

                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="border-b-2 border-gray-200">
                                                    <th className="text-left py-3 pr-4 text-gray-700 font-semibold">Shareholder</th>
                                                    <th className="text-right py-3 px-2 text-gray-700 font-semibold">Type</th>
                                                    <th className="text-right py-3 px-2 text-gray-700 font-semibold">Ownership %</th>
                                                    {shareholderExitBreakdown.isPreferred && (
                                                        <th className="text-right py-3 px-2 text-gray-700 font-semibold">Preference</th>
                                                    )}
                                                    <th className="text-right py-3 px-2 text-gray-700 font-semibold">Pro-Rata</th>
                                                    <th className="text-right py-3 px-2 text-gray-700 font-semibold">Total Return</th>
                                                    <th className="text-right py-3 pl-2 text-gray-700 font-semibold">% of Exit</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shareholderExitBreakdown.breakdown.map((shareholder, idx) => (
                                                    <tr 
                                                        key={shareholder.id} 
                                                        className={`border-b border-gray-100 hover:bg-gray-50 ${shareholder.isNew ? 'bg-green-50' : ''}`}
                                                    >
                                                        <td className="py-3 pr-4">
                                                            <div>
                                                                <p className={`font-medium ${shareholder.isNew ? 'text-green-700' : 'text-gray-900'}`}>
                                                                    {shareholder.name}
                                                                    {shareholder.isNew && (
                                                                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                                                            New
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                {shareholder.isNew && shareholder.roi !== undefined && (
                                                                    <p className={`text-xs mt-0.5 ${
                                                                        shareholder.roi >= 0 ? 'text-green-600' : 'text-red-600'
                                                                    }`}>
                                                                        ROI: {shareholder.roi >= 0 ? '+' : ''}{shareholder.roi.toFixed(0)}% 
                                                                        ({shareholder.multiple.toFixed(2)}x)
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-right py-3 px-2">
                                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                                shareholder.type === 'New' 
                                                                    ? 'bg-green-100 text-green-700' 
                                                                    : 'bg-blue-100 text-blue-700'
                                                            }`}>
                                                                {shareholder.type}
                                                            </span>
                                                        </td>
                                                        <td className="text-right py-3 px-2 font-medium">
                                                            {shareholder.ownershipPercent.toFixed(2)}%
                                                        </td>
                                                        {shareholderExitBreakdown.isPreferred && (
                                                            <td className="text-right py-3 px-2">
                                                                {shareholder.preferenceAmount > 0 ? (
                                                                    <span className="text-purple-700 font-semibold">
                                                                        {formatCurrencyWithCompanyCurrencyAndMagnitude(shareholder.preferenceAmount)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-400">—</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="text-right py-3 px-2">
                                                            {formatCurrencyWithCompanyCurrencyAndMagnitude(shareholder.proRataAmount)}
                                                        </td>
                                                        <td className="text-right py-3 px-2">
                                                            <p className={`font-bold ${shareholder.isNew ? 'text-green-700' : 'text-blue-700'}`}>
                                                                {formatCurrencyWithCompanyCurrencyAndMagnitude(shareholder.totalReturn)}
                                                            </p>
                                                        </td>
                                                        <td className="text-right py-3 pl-2 font-medium">
                                                            {shareholder.percentOfExit.toFixed(2)}%
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="border-t-2 border-gray-300 font-semibold">
                                                    <td className="py-3 pr-4 text-gray-900">Total</td>
                                                    <td className="text-right py-3 px-2"></td>
                                                    <td className="text-right py-3 px-2">100.00%</td>
                                                    {shareholderExitBreakdown.isPreferred && (
                                                        <td className="text-right py-3 px-2"></td>
                                                    )}
                                                    <td className="text-right py-3 px-2"></td>
                                                    <td className="text-right py-3 px-2 text-gray-900">
                                                        {formatCurrencyWithCompanyCurrencyAndMagnitude(shareholderExitBreakdown.exitValue)}
                                                    </td>
                                                    <td className="text-right py-3 pl-2">100.00%</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {shareholderExitBreakdown.isPreferred && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="flex items-start space-x-2">
                                                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-gray-600">
                                                    <strong>How to read this table:</strong> The "Preference" column shows liquidation 
                                                    preference paid first. The "Pro-Rata" column shows each shareholder's proportional 
                                                    share of the remaining proceeds. "Total Return" is the sum of both.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-white rounded-lg shadow-md p-12">
                            <div className="text-center">
                                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Ready to Model
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Enter a pre-money valuation and capital raise amount to see the impact on your cap table
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WhatIfScenarioPage;

